import { useMemo } from 'react'
import { skipToken } from '@reduxjs/toolkit/query'
import { useGetNetworkMembersQuery } from '@/core/services/network'
import { NetworkMemberAccessLevel } from '@/core/models/network.model'
import { useAppSelector } from '@/redux/rootReducer'
import { selectAuthUser } from '@/redux/reducers/auth.reducer'

export interface NetworkPermissions {
    isNetworkAdmin: boolean
    isNetworkCreator: boolean
    isNetworkMember: boolean
    hasDesmosConfigured: boolean
}

/**
 * Hook to check user permissions within a network
 * @param networkId The network ID to check permissions for
 * @param networkCreatorId The network creator ID (optional, if already known)
 * @param members Network members array (optional, if already known)
 * @returns Object containing permission flags and feature availability
 */
export function useNetworkMemberPermissions(
    { networkId, networkCreatorId, members }: {
        networkId?: string,
        networkCreatorId?: string,
        members?: string[]
    }
): NetworkPermissions {
    const authUser = useAppSelector(selectAuthUser)
    const { data: networkMembers } = useGetNetworkMembersQuery(networkId ?? skipToken, {
        skip: !networkId,
        refetchOnMountOrArgChange: true
    })

    return useMemo(() => {
        // Check if the user is a member
        const isNetworkMember = members
            ? !!members.find(member => member === authUser?._id)
            : !!networkMembers?.filter(member => !!member?.member)?.some(member => member.member._id === authUser?._id)

        // Check if the user is the creator
        const isNetworkCreator = networkCreatorId
            ? authUser?._id === networkCreatorId
            : false

        // Check if the user is an admin
        const userMember = networkMembers?.find(member => member?.member?._id === authUser?._id)
        const isNetworkAdmin = userMember?.accessLevel === NetworkMemberAccessLevel.ADMIN

        // Check if user's establishment has Desmos configured
        // Desmos is considered configured if establishment has desmosConfig OR finess number
        const hasDesmosConfigured = !!(
            authUser?.establishment?.desmosConfig ||
            authUser?.establishment?.finess
        )

        return {
            isNetworkAdmin: !!isNetworkAdmin,
            isNetworkCreator: !!isNetworkCreator,
            isNetworkMember: !!isNetworkMember,
            hasDesmosConfigured
        }
    }, [authUser, networkCreatorId, members, networkMembers])
} 